import { Alert, AlertDescription, AlertTitle, buttonVariants } from "@lagoon/ui";

import { describeDunning } from "../billing.js";
import { Link } from "../router.js";
import type { DunningFacts } from "../api.js";

/**
 * What an organisation in dunning sees, on every screen.
 *
 * **In the shell, not on the billing page**, and that is the whole design.
 * Somebody whose card has failed is not on the billing screen: they are on the
 * sites list wondering why the upload button refused them. A banner that only
 * appears where the remedy already is tells the people who least need telling.
 *
 * The copy comes from `describeDunning`, which is a pure function over the
 * `dunning` object and is where every wording decision is tested. This
 * component is the markup and nothing else, so a change to what the product
 * claims is blocked at each step is a change to one file with tests around it.
 *
 * Nothing is rendered for `none`, for an org that has never had a subscription,
 * or for a state this bundle does not recognise. See `describeDunning` for why
 * silence is the right answer to the last of those.
 *
 * `Alert` is a live region by default (`role="status"`, which is polite), which
 * is the property the previous `<output aria-live="polite">` was reaching for:
 * a change of billing state is not something the reader asked for, so it is
 * announced rather than left for them to notice, and it is important without
 * being an emergency that interrupts whatever they are typing.
 */
export function DunningBanner(props: {
  dunning: DunningFacts;
  /** Hidden on the billing screen itself: the remedy is already on the page. */
  showLink: boolean;
  navigate: (to: string) => void;
}): React.ReactElement | null {
  const notice = describeDunning(props.dunning);
  if (notice === null) return null;

  return (
    <Alert tone={notice.tone === "error" ? "danger" : "warning"}>
      <AlertTitle>{notice.heading}</AlertTitle>
      <AlertDescription className="lg:flex lg:flex-col lg:items-start lg:gap-3">
        <span>{notice.detail}</span>
        {props.showLink && notice.showBillingLink ? (
          <Link
            to="/billing"
            navigate={props.navigate}
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            Go to billing
          </Link>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
